export const SPORT_CONTENT = {
  'ice-skating': {
    value: 'skating',
    slug: 'ice-skating',
    name: 'Ice Skating',
    title: 'Ice Skating Program Management Software',
    metaDescription:
      'Streamline your ice skating program with Leevo. Manage figure skating classes, hockey programs, and learn-to-skate sessions with easy registration, skill tracking, and payment processing.',
    keywords: [
      'ice skating software',
      'figure skating management',
      'skating rink software',
      'learn to skate software',
      'ice rink registration',
      'skating class management',
      'hockey program software',
      'LTS USA software',
      'figure skating class registration',
      'ice arena management'
    ],
    hero: {
      headline: 'The Smarter Way to Run Your Ice Skating Program',
      subheadline:
        'From learn-to-skate classes to competitive figure skating, manage registrations, track skills, and process payments with software built for ice rinks.'
    },
    features: [
      {
        title: 'LTS USA Curriculum Support',
        description:
          'Teach Learn to Skate USA levels with built-in curriculum and skill tracking. Parents manage LTS USA memberships and renewals directly with LTS USA.'
      },
      {
        title: 'Skill Progression Tracking',
        description:
          'Track every skater from Basic 1 through Free Skate levels with visual progress reports that coaches and parents love.'
      },
      {
        title: 'Multi-Session Registration',
        description:
          'Let families register for figure skating, hockey, and recreational sessions all in one place with flexible scheduling options.'
      },
      {
        title: 'Skate Rental Management',
        description:
          'Track skate rentals by size and type, manage inventory, and streamline the check-in process for busy session days.'
      },
      {
        title: 'Automated Parent Communication',
        description:
          'Keep skating families informed with automated class reminders, progress updates, and important announcements.'
      },
      {
        title: 'Integrated Payment Processing',
        description:
          'Accept payments online with Stripe integration. Offer payment plans, process refunds, and manage seasonal billing effortlessly.'
      }
    ],
    faqs: [
      {
        question: 'Does Leevo support Learn to Skate USA (LTS USA) programs?',
        answer:
          'Yes. Leevo supports LTS USA curriculum and skill tracking from Basic through Free Skate levels and collects membership numbers during class registration. Parents register, renew, and pay for memberships directly with LTS USA. Leevo does not verify membership status or handle renewals.'
      },
      {
        question: 'Can I manage both figure skating and hockey programs in Leevo?',
        answer:
          'Absolutely. Leevo supports multiple curriculums within a single facility, so you can manage figure skating, hockey, recreational skating, and adult programs all from one dashboard.'
      },
      {
        question: 'How does skill tracking work for ice skating programs?',
        answer:
          'Coaches can track individual skills for each skater during class. Parents receive progress reports showing which skills have been mastered and which are in progress, making it easy to see improvement over time.'
      },
      {
        question: 'Can parents register and pay online for skating classes?',
        answer:
          'Yes, parents can browse available classes, register their skaters, and pay online through a mobile-friendly registration portal. You can also offer payment plans for seasonal programs.'
      },
      {
        question: 'Does Leevo handle skate rentals?',
        answer:
          'Yes, Leevo includes skate rental tracking so you can manage inventory by size and type, track which students need rentals, and streamline your rental operations.'
      }
    ],
    benefits: [
      'Reduce administrative time by 50% with automated registrations',
      'Increase enrollment with easy online registration',
      'Keep parents engaged with real-time skill tracking',
      'Eliminate double-bookings with smart class scheduling',
      'Process payments securely with Stripe integration'
    ]
  },

  gymnastics: {
    value: 'gymnastics',
    slug: 'gymnastics',
    name: 'Gymnastics',
    title: 'Gymnastics Class Management Software',
    metaDescription:
      'Manage your gymnastics gym with Leevo. Handle class registrations, track skill progressions, and process payments for recreational and competitive gymnastics programs.',
    keywords: [
      'gymnastics software',
      'gymnastics class management',
      'gym management software',
      'gymnastics registration',
      'gymnastics skill tracking',
      'recreational gymnastics software',
      'competitive gymnastics management',
      'tumbling class software',
      'gymnastics gym software',
      'youth gymnastics programs'
    ],
    hero: {
      headline: 'Powerful Software for Growing Gymnastics Programs',
      subheadline:
        'From tiny tumblers to competitive athletes, manage class registrations, track skill progressions, and grow your gymnastics program with confidence.'
    },
    features: [
      {
        title: 'Level-Based Skill Tracking',
        description:
          'Track gymnast progression through recreational and competitive levels with detailed skill checklists that coaches can update in real-time.'
      },
      {
        title: 'Flexible Class Scheduling',
        description:
          'Create classes for all ages and levels, manage gym space allocation, and handle make-up classes without the scheduling headaches.'
      },
      {
        title: 'Parent Portal',
        description:
          'Give parents visibility into their gymnast progress, upcoming classes, and account balance through an easy-to-use mobile portal.'
      },
      {
        title: 'Multi-Program Support',
        description:
          'Manage recreational classes, competitive teams, camps, clinics, and birthday parties all from one platform.'
      },
      {
        title: 'Automated Billing',
        description:
          'Set up monthly tuition, process payments automatically, and handle pro-rated fees for mid-session enrollments.'
      },
      {
        title: 'Waitlist Management',
        description: 'Automatically manage waitlists for popular classes and notify families when spots open up.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo track gymnastics skills and levels?',
        answer:
          'Yes, Leevo allows you to create custom skill checklists for each level of your program. Coaches can track individual skills during class, and parents can see progress reports showing mastery and areas for improvement.'
      },
      {
        question: 'Does Leevo support both recreational and competitive gymnastics?',
        answer:
          'Absolutely. You can create separate programs for recreational classes, competitive teams, and specialized training. Each can have its own curriculum, pricing, and scheduling.'
      },
      {
        question: 'How do parents register for gymnastics classes?',
        answer:
          'Parents can browse available classes by age, level, and schedule through your branded registration portal. They can register, pay, and manage their account online without any phone calls or paperwork.'
      },
      {
        question: 'Can I manage camps and clinics in addition to regular classes?',
        answer:
          'Yes, Leevo supports one-time events like camps, clinics, and birthday parties alongside your regular class schedule. You can set different pricing, capacity limits, and registration windows for each.'
      },
      {
        question: 'Does Leevo handle automatic monthly billing?',
        answer:
          'Yes, you can set up recurring monthly tuition payments that process automatically. Families can also set up payment plans for seasonal fees or competition costs.'
      }
    ],
    benefits: [
      'Save 10+ hours per week on administrative tasks',
      'Reduce missed payments with automated billing',
      'Keep gymnasts progressing with clear skill tracking',
      'Fill classes faster with online registration',
      'Communicate effortlessly with parents through the portal'
    ]
  },

  swimming: {
    value: 'swimming',
    slug: 'swimming',
    name: 'Swimming',
    title: 'Swim School Management Software',
    metaDescription:
      'Run your swim school efficiently with Leevo. Manage swim lesson registrations, track swimmer progress, and process payments for lessons, teams, and aquatic programs.',
    keywords: [
      'swim school software',
      'swimming lesson management',
      'swim lesson registration',
      'aquatic program software',
      'swim team management',
      'learn to swim software',
      'pool management software',
      'swimming class registration',
      'swim instructor software',
      'youth swimming programs'
    ],
    hero: {
      headline: 'Streamline Your Swim School Operations',
      subheadline:
        'From infant swim classes to competitive teams, manage registrations, track water safety skills, and keep your swim program running smoothly.'
    },
    features: [
      {
        title: 'Water Safety Skill Tracking',
        description:
          'Track swimmer progression through Red Cross or custom swim levels with skill checklists focused on water safety and stroke development.'
      },
      {
        title: 'Lane & Pool Scheduling',
        description:
          'Manage pool space efficiently with visual scheduling that shows lane assignments, class times, and instructor availability.'
      },
      {
        title: 'Ratio Management',
        description:
          'Maintain safe student-to-instructor ratios with automatic capacity limits and waitlist management for each class level.'
      },
      {
        title: 'Session-Based Registration',
        description:
          'Run seasonal sessions with easy registration periods, early bird pricing, and automatic class advancement between sessions.'
      },
      {
        title: 'Parent Communication',
        description:
          'Keep swim families informed about class cancellations, weather closures, and their child progress through automated messages.'
      },
      {
        title: 'Make-Up Class Management',
        description: 'Offer and track make-up classes for missed lessons without the scheduling confusion.'
      }
    ],
    faqs: [
      {
        question: 'Does Leevo support different swim lesson curricula?',
        answer:
          'Yes, Leevo supports Red Cross Learn-to-Swim, YMCA, and custom swim curricula. You can create your own skill levels and track progression through each stage of water competency.'
      },
      {
        question: 'Can I manage swim team and lesson programs together?',
        answer:
          'Absolutely. Leevo handles recreational lessons, competitive teams, adult programs, and specialty classes like water aerobics all within the same system.'
      },
      {
        question: 'How does Leevo help maintain safe instructor-to-student ratios?',
        answer:
          'You can set capacity limits for each class based on level and age. The system automatically enforces these limits during registration and manages waitlists when classes are full.'
      },
      {
        question: 'Can parents see their child swim progress?',
        answer:
          'Yes, parents have access to a portal where they can view skill progress, see upcoming classes, and track which water safety skills their child has mastered.'
      },
      {
        question: 'Does Leevo handle seasonal swim registrations?',
        answer:
          'Yes, you can set up registration periods for each swim season with early bird pricing, member discounts, and automatic session transitions.'
      }
    ],
    benefits: [
      'Fill swim classes faster with online registration',
      'Maintain safety standards with ratio management',
      'Track water safety skills with detailed progress reports',
      'Reduce no-shows with automated reminders',
      'Process payments securely with flexible billing options'
    ]
  },

  soccer: {
    value: 'soccer',
    slug: 'soccer',
    name: 'Soccer',
    title: 'Soccer Academy Management Software',
    metaDescription:
      'Manage your soccer academy or youth league with Leevo. Handle team registrations, track player development, and streamline operations for recreational and competitive programs.',
    keywords: [
      'soccer software',
      'soccer academy management',
      'youth soccer registration',
      'soccer team management',
      'soccer club software',
      'soccer league registration',
      'player development tracking',
      'soccer training software',
      'recreational soccer software',
      'competitive soccer management'
    ],
    hero: {
      headline: 'Score Big with Better Soccer Program Management',
      subheadline:
        'From youth rec leagues to competitive academies, manage registrations, track player development, and run your soccer program like a pro.'
    },
    features: [
      {
        title: 'Team & League Management',
        description:
          'Organize players into teams, manage age divisions, and handle league registrations with flexible roster management.'
      },
      {
        title: 'Player Development Tracking',
        description:
          'Track individual player skills, from ball control to tactical awareness, with progress reports coaches and parents can review.'
      },
      {
        title: 'Season Registration',
        description:
          'Run seasonal registrations with age-based divisions, skill assessments, and team placement workflows.'
      },
      {
        title: 'Multi-Program Support',
        description:
          'Manage recreational leagues, travel teams, camps, clinics, and private training all in one platform.'
      },
      {
        title: 'Parent Portal',
        description:
          'Give families a central place to register, view schedules, track player progress, and manage payments.'
      },
      {
        title: 'Communication Tools',
        description: 'Send team announcements, practice reminders, and game updates to keep everyone on the same page.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage both recreational and competitive soccer programs?',
        answer:
          'Yes, Leevo supports recreational leagues, travel teams, academy programs, and elite training. You can create separate programs with different pricing, schedules, and skill requirements.'
      },
      {
        question: 'How does player registration work for soccer leagues?',
        answer:
          'Parents can register online, select age divisions, and pay registration fees through your branded portal. You can also collect player information, medical forms, and waivers during registration.'
      },
      {
        question: 'Can I track player skill development in Leevo?',
        answer:
          'Yes, coaches can track individual skills for each player using customizable skill checklists. Parents receive progress reports showing their child development over time.'
      },
      {
        question: 'Does Leevo support camps and clinics?',
        answer:
          'Absolutely. You can create one-time events like summer camps, holiday clinics, and skills workshops alongside your regular season programs.'
      },
      {
        question: 'Can I manage multiple teams and age groups?',
        answer:
          'Yes, Leevo handles multiple teams, age divisions, and skill levels. You can organize players into rosters and manage each team schedule and communications.'
      }
    ],
    benefits: [
      'Streamline seasonal registrations with online signup',
      'Track player development across seasons',
      'Reduce administrative burden with automated processes',
      'Keep families engaged with real-time updates',
      'Process payments and manage fees effortlessly'
    ]
  },

  basketball: {
    value: 'basketball',
    slug: 'basketball',
    name: 'Basketball',
    title: 'Basketball Program Management Software',
    metaDescription:
      'Run your basketball league or training program with Leevo. Manage registrations, track player skills, and handle payments for youth leagues, AAU teams, and training academies.',
    keywords: [
      'basketball software',
      'basketball league management',
      'youth basketball registration',
      'basketball training software',
      'AAU basketball software',
      'basketball camp registration',
      'basketball skills tracking',
      'rec league software',
      'basketball academy management',
      'player development software'
    ],
    hero: {
      headline: 'Take Your Basketball Program to the Next Level',
      subheadline:
        'From youth rec leagues to elite training academies, manage registrations, track player development, and grow your basketball program.'
    },
    features: [
      {
        title: 'League Registration',
        description: 'Handle seasonal registrations with age divisions, skill levels, and team placements all online.'
      },
      {
        title: 'Player Skill Tracking',
        description:
          'Track individual skills from ball handling to shooting technique with progress reports for players and parents.'
      },
      {
        title: 'Training Program Management',
        description: 'Manage clinics, camps, and private training sessions alongside league play.'
      },
      {
        title: 'Team Organization',
        description: 'Create teams, manage rosters, and handle player placements with flexible organizational tools.'
      },
      {
        title: 'Payment Processing',
        description:
          'Accept registration fees, process equipment deposits, and handle payment plans with secure online payments.'
      },
      {
        title: 'Parent Communication',
        description: 'Keep families informed with practice schedules, game updates, and program announcements.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo handle recreational and competitive basketball programs?',
        answer:
          'Yes, Leevo supports rec leagues, travel teams, AAU programs, and training academies. Each program can have its own registration, pricing, and curriculum.'
      },
      {
        question: 'How do parents register for basketball leagues?',
        answer:
          'Parents register online through your branded portal, selecting age divisions and paying fees. You can collect player information, medical forms, and waivers during registration.'
      },
      {
        question: 'Can I track basketball skill development?',
        answer:
          'Yes, create custom skill checklists for shooting, dribbling, defense, and more. Coaches update progress during practices, and parents see development reports.'
      },
      {
        question: 'Does Leevo support basketball camps and clinics?',
        answer:
          'Absolutely. Create camp registrations, skills clinics, and specialty training programs with different pricing and schedules from your regular league.'
      },
      {
        question: 'Can I manage multiple teams and age groups?',
        answer:
          'Yes, organize players by age, skill level, and team with flexible roster management and communication tools for each group.'
      }
    ],
    benefits: [
      'Fill rosters faster with online registration',
      'Track player growth with skill assessments',
      'Reduce admin work with automated processes',
      'Keep families connected with easy communication',
      'Process payments securely online'
    ]
  },

  baseball: {
    value: 'baseball',
    slug: 'baseball',
    name: 'Baseball',
    title: 'Baseball League & Training Software',
    metaDescription:
      'Manage your baseball league or training academy with Leevo. Handle team registrations, track player development, and streamline operations for youth and travel baseball.',
    keywords: [
      'baseball software',
      'baseball league management',
      'youth baseball registration',
      'baseball training software',
      'travel baseball software',
      'baseball camp registration',
      'little league software',
      'baseball academy management',
      'player development tracking',
      'baseball team management'
    ],
    hero: {
      headline: 'Hit a Home Run with Better Baseball Management',
      subheadline:
        'From Little League to travel ball, manage registrations, track player skills, and run your baseball program efficiently.'
    },
    features: [
      {
        title: 'Season Registration',
        description:
          'Run spring, summer, and fall registrations with age-based divisions and online payment processing.'
      },
      {
        title: 'Player Development',
        description:
          'Track hitting, pitching, fielding, and base running skills with coach assessments and progress reports.'
      },
      {
        title: 'Team Management',
        description: 'Create teams, manage rosters, and handle draft or tryout placements.'
      },
      {
        title: 'Training Programs',
        description: 'Manage batting cage rentals, private lessons, and skills clinics alongside league play.'
      },
      {
        title: 'Equipment & Uniforms',
        description: 'Track uniform orders, equipment needs, and collect sizing information during registration.'
      },
      {
        title: 'Family Portal',
        description: 'Give families access to schedules, standings, and their player progress all in one place.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage Little League and travel baseball?',
        answer:
          'Yes, Leevo handles recreational leagues like Little League and competitive travel programs. Create separate registrations, pricing, and schedules for each.'
      },
      {
        question: 'How does baseball registration work?',
        answer:
          'Parents register online, select age divisions, and pay fees through your portal. Collect birth certificates, medical forms, and uniform sizes during registration.'
      },
      {
        question: 'Can I track player skill development?',
        answer:
          'Yes, coaches can assess batting, pitching, fielding, and other skills. Parents see progress reports showing improvement over the season.'
      },
      {
        question: 'Does Leevo support camps and clinics?',
        answer:
          'Absolutely. Create summer camps, hitting clinics, pitching workshops, and more with separate registration and pricing.'
      },
      {
        question: 'Can I manage equipment and uniform orders?',
        answer:
          'Yes, collect sizing information during registration and track uniform and equipment distribution for each team.'
      }
    ],
    benefits: [
      'Streamline seasonal registration',
      'Track player development year over year',
      'Manage multiple programs in one place',
      'Communicate easily with families',
      'Process payments securely'
    ]
  },

  volleyball: {
    value: 'volleyball',
    slug: 'volleyball',
    name: 'Volleyball',
    title: 'Volleyball Club Management Software',
    metaDescription:
      'Manage your volleyball club or league with Leevo. Handle tryouts, registrations, skill tracking, and payments for recreational and club volleyball programs.',
    keywords: [
      'volleyball software',
      'volleyball club management',
      'volleyball registration',
      'club volleyball software',
      'volleyball tryout management',
      'volleyball training software',
      'youth volleyball programs',
      'volleyball league software',
      'player development tracking',
      'volleyball camp registration'
    ],
    hero: {
      headline: 'Elevate Your Volleyball Program',
      subheadline:
        'From beginner clinics to elite club teams, manage tryouts, registrations, and player development with powerful volleyball software.'
    },
    features: [
      {
        title: 'Tryout Management',
        description: 'Run organized tryouts with registration, skill assessments, and team placement workflows.'
      },
      {
        title: 'Club Registration',
        description: 'Handle club season registrations with team fees, uniform orders, and tournament schedules.'
      },
      {
        title: 'Skill Assessment',
        description: 'Track serving, passing, setting, hitting, and defensive skills with coach evaluations.'
      },
      {
        title: 'Training Programs',
        description: 'Manage clinics, private lessons, and position-specific training alongside team practices.'
      },
      {
        title: 'Tournament Tracking',
        description: 'Keep families informed about tournament schedules, locations, and team commitments.'
      },
      {
        title: 'Multi-Team Support',
        description: 'Manage multiple teams by age and skill level with separate rosters and communications.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage volleyball club tryouts?',
        answer:
          'Yes, set up tryout registration, schedule evaluation sessions, track player assessments, and manage team placements all in one system.'
      },
      {
        question: 'How do families register for volleyball club?',
        answer:
          'Parents register online, select tryout sessions or team programs, and pay fees. Collect player information, medical forms, and commitment agreements.'
      },
      {
        question: 'Can I track volleyball skills?',
        answer:
          'Yes, create skill checklists for serving, passing, setting, attacking, and defense. Coaches update progress and parents see development reports.'
      },
      {
        question: 'Does Leevo support recreational volleyball leagues?',
        answer:
          'Absolutely. Manage recreational leagues alongside club programs with different registrations, pricing, and skill levels.'
      },
      {
        question: 'Can I manage camps and clinics?',
        answer:
          'Yes, create volleyball camps, position clinics, and skills workshops with separate registration and scheduling.'
      }
    ],
    benefits: [
      'Streamline tryout processes',
      'Manage club operations efficiently',
      'Track player development',
      'Keep families informed',
      'Process fees and payments online'
    ]
  },

  tennis: {
    value: 'tennis',
    slug: 'tennis',
    name: 'Tennis',
    title: 'Tennis Program Management Software',
    metaDescription:
      'Run your tennis program with Leevo. Manage lesson registrations, court scheduling, and player development for clubs, academies, and recreational programs.',
    keywords: [
      'tennis software',
      'tennis lesson management',
      'tennis club software',
      'tennis academy management',
      'tennis registration',
      'court scheduling software',
      'tennis camp registration',
      'junior tennis programs',
      'tennis instruction software',
      'player development tracking'
    ],
    hero: {
      headline: 'Ace Your Tennis Program Management',
      subheadline:
        'From beginner lessons to advanced training, manage registrations, schedule courts, and track player development effortlessly.'
    },
    features: [
      {
        title: 'Lesson Registration',
        description:
          'Let players register for group lessons, private instruction, and clinics online with easy scheduling.'
      },
      {
        title: 'Court Scheduling',
        description: 'Manage court assignments for lessons, clinics, and open play with visual scheduling tools.'
      },
      {
        title: 'Skill Progression',
        description:
          'Track player development through beginner, intermediate, and advanced levels with detailed skill assessments.'
      },
      {
        title: 'Program Variety',
        description: 'Manage junior programs, adult lessons, cardio tennis, and competitive training in one system.'
      },
      {
        title: 'Pro Management',
        description: 'Track instructor schedules, lesson assignments, and availability across your teaching staff.'
      },
      {
        title: 'Membership Integration',
        description: 'Handle member vs. non-member pricing and track lesson packages for your club.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage tennis lessons and clinics?',
        answer:
          'Yes, handle group lessons, private instruction, drills clinics, and specialty programs all with online registration and scheduling.'
      },
      {
        question: 'How does tennis registration work?',
        answer:
          'Players or parents register online, select lesson times, and pay fees. Set different pricing for members, non-members, and package deals.'
      },
      {
        question: 'Can I track tennis skill development?',
        answer:
          'Yes, create skill assessments for strokes, strategy, and match play. Track progression from beginner through advanced levels.'
      },
      {
        question: 'Does Leevo support junior and adult programs?',
        answer:
          'Absolutely. Manage separate programs for juniors, adults, seniors, and competitive players with appropriate skill tracking for each.'
      },
      {
        question: 'Can I manage multiple tennis pros?',
        answer:
          'Yes, track instructor schedules, lesson assignments, and availability. Parents can select preferred pros during registration.'
      }
    ],
    benefits: [
      'Fill lessons with online registration',
      'Manage courts and schedules efficiently',
      'Track player improvement',
      'Handle flexible pricing',
      'Communicate with players easily'
    ]
  },

  golf: {
    value: 'golf',
    slug: 'golf',
    name: 'Golf',
    title: 'Golf Instruction Management Software',
    metaDescription:
      'Manage your golf instruction program with Leevo. Handle lesson registrations, track player development, and run junior golf camps and clinics efficiently.',
    keywords: [
      'golf software',
      'golf lesson management',
      'junior golf programs',
      'golf instruction software',
      'golf academy management',
      'golf camp registration',
      'golf clinic software',
      'player development tracking',
      'golf school software',
      'golf coaching software'
    ],
    hero: {
      headline: 'Drive Your Golf Program Forward',
      subheadline:
        'From junior clinics to advanced instruction, manage lesson registrations, track player progress, and grow your golf program.'
    },
    features: [
      {
        title: 'Lesson Registration',
        description: 'Let golfers register for private lessons, group clinics, and junior programs online.'
      },
      {
        title: 'Skill Tracking',
        description:
          'Track development in full swing, short game, putting, and course management with progress reports.'
      },
      {
        title: 'Junior Programs',
        description: 'Manage age-appropriate junior golf programs from introduction through competitive junior golf.'
      },
      {
        title: 'Camp Management',
        description: 'Run summer camps, holiday clinics, and skills workshops with easy registration and scheduling.'
      },
      {
        title: 'Instructor Scheduling',
        description: 'Manage pro schedules, lesson availability, and teaching assignments.'
      },
      {
        title: 'Equipment Tracking',
        description: 'Track loaner club programs and equipment needs for junior golfers.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage golf lessons and clinics?',
        answer:
          'Yes, handle private lessons, group instruction, junior programs, and specialty clinics all with online registration.'
      },
      {
        question: 'How do golfers register for instruction?',
        answer:
          'Players or parents register online, select lesson types and times, and pay fees. Offer package pricing and membership rates.'
      },
      {
        question: 'Can I track golf skill development?',
        answer:
          'Yes, create assessments for swing mechanics, short game, putting, and course management. Track improvement over time.'
      },
      {
        question: 'Does Leevo support junior golf programs?',
        answer:
          'Absolutely. Create age-appropriate programs from First Tee-style introduction through competitive junior golf with skill-based progression.'
      },
      {
        question: 'Can I manage golf camps?',
        answer:
          'Yes, create summer camps, holiday clinics, and intensive training programs with separate registration and pricing.'
      }
    ],
    benefits: [
      'Fill lessons with online booking',
      'Track player development',
      'Manage junior programs efficiently',
      'Run camps and clinics easily',
      'Process payments online'
    ]
  },

  'martial-arts': {
    value: 'martial_arts',
    slug: 'martial-arts',
    name: 'Martial Arts',
    title: 'Martial Arts School Management Software',
    metaDescription:
      'Run your martial arts school with Leevo. Manage student registrations, track belt progressions, and handle payments for karate, taekwondo, jiu-jitsu, and more.',
    keywords: [
      'martial arts software',
      'dojo management software',
      'karate school software',
      'taekwondo management',
      'jiu jitsu software',
      'belt tracking software',
      'martial arts registration',
      'martial arts billing',
      'student management software',
      'martial arts school management'
    ],
    hero: {
      headline: 'Powerful Software for Martial Arts Schools',
      subheadline:
        'From white belt to black belt, manage student registrations, track rank progressions, and run your dojo with discipline and efficiency.'
    },
    features: [
      {
        title: 'Belt & Rank Tracking',
        description:
          'Track student progression through belt ranks with skill requirements, testing dates, and promotion history.'
      },
      {
        title: 'Class Scheduling',
        description: 'Manage classes by age, rank, and discipline with flexible scheduling for your training schedule.'
      },
      {
        title: 'Automated Billing',
        description:
          'Set up monthly tuition, process payments automatically, and handle testing fees and equipment purchases.'
      },
      {
        title: 'Testing Management',
        description: 'Schedule belt tests, track student readiness, and manage promotion ceremonies.'
      },
      {
        title: 'Multi-Program Support',
        description: 'Manage multiple martial arts styles, kids classes, adult programs, and competition teams.'
      },
      {
        title: 'Parent Portal',
        description: 'Give families visibility into their student rank progression, upcoming tests, and account status.'
      }
    ],
    faqs: [
      {
        question: 'Does Leevo support different martial arts styles?',
        answer:
          'Yes, Leevo works for karate, taekwondo, jiu-jitsu, judo, kung fu, MMA, and other martial arts. Customize belt ranks and skill requirements for your specific style.'
      },
      {
        question: 'How does belt tracking work?',
        answer:
          'Create your belt ranking system with required skills for each level. Track student progress, schedule testing, and record promotions with dates and certificates.'
      },
      {
        question: 'Can parents register and pay online?',
        answer:
          'Yes, parents can register new students, sign up for classes, and pay tuition online. Set up automatic monthly billing or payment plans.'
      },
      {
        question: 'Can I manage testing and promotions?',
        answer:
          'Yes, schedule belt tests, track which students are ready, manage testing fees, and record promotion results all in one system.'
      },
      {
        question: 'Does Leevo handle competition teams?',
        answer:
          'Yes, manage regular classes alongside competition training, track tournament results, and handle team registrations and fees.'
      }
    ],
    benefits: [
      'Track belt progressions accurately',
      'Automate tuition billing',
      'Reduce administrative work',
      'Keep students engaged with progress tracking',
      'Manage testing and promotions efficiently'
    ]
  },

  dance: {
    value: 'dance',
    slug: 'dance',
    name: 'Dance',
    title: 'Dance Studio Management Software',
    metaDescription:
      'Manage your dance studio with Leevo. Handle class registrations, track dancer progress, and process payments for ballet, jazz, hip hop, and all dance styles.',
    keywords: [
      'dance studio software',
      'dance class management',
      'dance school registration',
      'ballet software',
      'dance studio billing',
      'dance recital management',
      'dance studio scheduling',
      'dancer progress tracking',
      'dance class registration',
      'dance academy software'
    ],
    hero: {
      headline: 'Beautiful Software for Dance Studios',
      subheadline:
        'From tiny dancers to advanced performers, manage class registrations, track progress, and run your dance studio with grace and efficiency.'
    },
    features: [
      {
        title: 'Class Registration',
        description: 'Let families register for ballet, jazz, tap, hip hop, contemporary, and all dance styles online.'
      },
      {
        title: 'Level Progression',
        description:
          'Track dancer development through technique levels with skill assessments and placement recommendations.'
      },
      {
        title: 'Recital Management',
        description: 'Organize recital registrations, costume fees, and performance schedules all in one place.'
      },
      {
        title: 'Multi-Class Enrollment',
        description: 'Handle dancers enrolled in multiple classes with combined billing and family discounts.'
      },
      {
        title: 'Costume & Fee Tracking',
        description:
          'Collect costume deposits, track sizing, and manage additional fees for competitions and performances.'
      },
      {
        title: 'Automated Tuition',
        description: 'Set up monthly billing that adjusts automatically for multiple classes and sibling discounts.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage multiple dance styles?',
        answer:
          'Yes, handle ballet, jazz, tap, hip hop, contemporary, lyrical, acro, and any other dance style with separate classes, levels, and pricing.'
      },
      {
        question: 'How do families register for dance classes?',
        answer:
          'Parents browse classes by style, age, and level, then register and pay online. Handle multiple classes per dancer with combined checkout.'
      },
      {
        question: 'Can I track dancer skill progression?',
        answer:
          'Yes, create technique assessments for each style and level. Track development and recommend placements for the next season.'
      },
      {
        question: 'Does Leevo handle recital management?',
        answer: 'Yes, manage recital sign-ups, collect costume fees and sizing, and coordinate performance schedules.'
      },
      {
        question: 'Can I offer sibling and multi-class discounts?',
        answer:
          'Absolutely. Set up automatic discounts for siblings and students enrolled in multiple classes. Tuition adjusts automatically.'
      }
    ],
    benefits: [
      'Fill classes with easy online registration',
      'Track dancer development',
      'Simplify recital organization',
      'Automate tuition and billing',
      'Keep dance families engaged'
    ]
  },

  cheerleading: {
    value: 'cheerleading',
    slug: 'cheerleading',
    name: 'Cheerleading',
    title: 'Cheer Gym Management Software',
    metaDescription:
      'Manage your cheer gym with Leevo. Handle team registrations, track skill progressions, and process payments for recreational and competitive cheerleading programs.',
    keywords: [
      'cheerleading software',
      'cheer gym management',
      'all star cheer software',
      'cheerleading registration',
      'cheer team management',
      'competitive cheer software',
      'cheer skill tracking',
      'cheer gym billing',
      'cheerleading program management',
      'youth cheer software'
    ],
    hero: {
      headline: 'Rally Your Cheer Program to Success',
      subheadline:
        'From rec cheer to competitive all-star teams, manage registrations, track skills, and run your cheer gym with energy and efficiency.'
    },
    features: [
      {
        title: 'Team Registration',
        description: 'Handle tryouts, team placements, and season registrations for all-star and recreational cheer.'
      },
      {
        title: 'Skill Tracking',
        description: 'Track tumbling, stunting, jumps, and dance skills with level-appropriate assessments.'
      },
      {
        title: 'Competition Management',
        description: 'Organize competition fees, travel arrangements, and team schedules for the competition season.'
      },
      {
        title: 'Uniform & Apparel',
        description: 'Collect sizing, track uniform orders, and manage apparel purchases for athletes.'
      },
      {
        title: 'Multi-Program Support',
        description: 'Manage all-star teams, school cheer, tumbling classes, and camps in one system.'
      },
      {
        title: 'Parent Communication',
        description: 'Keep cheer families informed about practices, competitions, and team announcements.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage all-star and recreational cheer?',
        answer:
          'Yes, handle competitive all-star teams alongside recreational cheer programs with separate registrations, pricing, and skill tracking.'
      },
      {
        question: 'How do families register for cheer?',
        answer:
          'Parents can register for tryouts, team placements, or rec classes online. Collect athlete information, medical forms, and sizing during registration.'
      },
      {
        question: 'Can I track cheerleading skills?',
        answer:
          'Yes, create skill checklists for tumbling, stunting, jumps, and dance. Track individual athlete progress and team readiness.'
      },
      {
        question: 'Does Leevo handle competition fees?',
        answer:
          'Yes, collect competition registration, travel costs, and additional fees through the parent portal with payment plans available.'
      },
      {
        question: 'Can I manage tumbling classes too?',
        answer:
          'Absolutely. Run tumbling classes, private lessons, and clinics alongside team practices with separate scheduling and pricing.'
      }
    ],
    benefits: [
      'Streamline tryout processes',
      'Track athlete skill development',
      'Manage competition logistics',
      'Automate billing and fees',
      'Keep families connected'
    ]
  },

  'track-and-field': {
    value: 'track_and_field',
    slug: 'track-and-field',
    name: 'Track & Field',
    title: 'Track and Field Club Management Software',
    metaDescription:
      'Manage your track and field club with Leevo. Handle registrations, track athlete development, and streamline operations for youth and competitive track programs.',
    keywords: [
      'track and field software',
      'track club management',
      'youth track registration',
      'track meet management',
      'running club software',
      'athletic program software',
      'track training software',
      'cross country software',
      'athlete development tracking',
      'track team management'
    ],
    hero: {
      headline: 'Run Your Track Program Like a Champion',
      subheadline:
        'From youth track clubs to competitive teams, manage registrations, track athlete progress, and cross the finish line with confidence.'
    },
    features: [
      {
        title: 'Season Registration',
        description: 'Handle seasonal registrations for track and cross country with age group divisions.'
      },
      {
        title: 'Event Tracking',
        description: 'Track athlete progress in sprints, distance, jumps, throws, and multi-events.'
      },
      {
        title: 'Meet Management',
        description: 'Organize meet entries, track results, and communicate meet schedules with families.'
      },
      {
        title: 'Training Groups',
        description: 'Organize athletes into training groups by event, age, or skill level.'
      },
      {
        title: 'Performance Records',
        description: 'Track personal bests, season records, and athlete improvement over time.'
      },
      {
        title: 'Multi-Sport Support',
        description: 'Manage track and cross country seasons with unified athlete profiles.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage track and cross country together?',
        answer: 'Yes, manage both track and cross country seasons with shared athlete profiles and unified billing.'
      },
      {
        question: 'How do athletes register for track club?',
        answer:
          'Parents register online, select events or training groups, and pay fees. Collect medical information and waivers during registration.'
      },
      {
        question: 'Can I track athlete performances?',
        answer:
          'Yes, record times, distances, and heights for each event. Track personal bests and see improvement over the season.'
      },
      {
        question: 'Does Leevo support different age groups?',
        answer: 'Absolutely. Organize athletes by age division with appropriate events and training for each level.'
      },
      {
        question: 'Can I manage track meets?',
        answer: 'Yes, communicate meet schedules, track entries, and share results with families.'
      }
    ],
    benefits: [
      'Streamline registration',
      'Track athlete development',
      'Organize training groups',
      'Communicate meet schedules',
      'Record performances'
    ]
  },

  football: {
    value: 'football',
    slug: 'football',
    name: 'Football',
    title: 'Youth Football Management Software',
    metaDescription:
      'Manage your youth football league or training program with Leevo. Handle team registrations, track player development, and streamline operations.',
    keywords: [
      'youth football software',
      'football league management',
      'football registration',
      'flag football software',
      'tackle football management',
      'football team software',
      'youth sports registration',
      'football camp registration',
      'player safety tracking',
      'football program management'
    ],
    hero: {
      headline: 'Win with Better Football Program Management',
      subheadline:
        'From flag football to tackle leagues, manage registrations, track player development, and run your football program efficiently.'
    },
    features: [
      {
        title: 'League Registration',
        description: 'Handle seasonal registrations with age and weight divisions for flag and tackle football.'
      },
      {
        title: 'Player Development',
        description: 'Track skills in passing, catching, blocking, tackling, and position-specific fundamentals.'
      },
      {
        title: 'Team Management',
        description: 'Create teams, manage rosters, and handle draft or tryout placements.'
      },
      {
        title: 'Safety Compliance',
        description: 'Track required certifications, medical clearances, and safety training completion.'
      },
      {
        title: 'Equipment Tracking',
        description: 'Manage equipment distribution, sizing, and collection at season end.'
      },
      {
        title: 'Parent Communication',
        description: 'Keep families informed about practices, games, and program updates.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage flag and tackle football?',
        answer:
          'Yes, handle both flag and tackle programs with appropriate age groups, registrations, and safety requirements.'
      },
      {
        question: 'How do families register for football?',
        answer:
          'Parents register online, select divisions, and pay fees. Collect player information, medical forms, and equipment sizing.'
      },
      {
        question: 'Can I track player skill development?',
        answer: 'Yes, create position-specific skill assessments and track player improvement over the season.'
      },
      {
        question: 'Does Leevo support camps and clinics?',
        answer: 'Absolutely. Run summer camps, skills clinics, and combines alongside your regular league.'
      },
      {
        question: 'Can I manage equipment distribution?',
        answer: 'Yes, track equipment sizing, distribution, and collection with player-specific records.'
      }
    ],
    benefits: [
      'Streamline registration',
      'Track player development',
      'Manage safety compliance',
      'Handle equipment logistics',
      'Keep families connected'
    ]
  },

  lacrosse: {
    value: 'lacrosse',
    slug: 'lacrosse',
    name: 'Lacrosse',
    title: 'Lacrosse Club Management Software',
    metaDescription:
      'Manage your lacrosse club or league with Leevo. Handle team registrations, track player development, and streamline operations for youth and travel lacrosse.',
    keywords: [
      'lacrosse software',
      'lacrosse club management',
      'youth lacrosse registration',
      'lacrosse team software',
      'travel lacrosse management',
      'lacrosse training software',
      'lacrosse camp registration',
      'player development tracking',
      'lacrosse league software',
      'lacrosse program management'
    ],
    hero: {
      headline: 'Take Your Lacrosse Program to the Next Level',
      subheadline:
        'From youth leagues to travel teams, manage registrations, track player skills, and grow your lacrosse program.'
    },
    features: [
      {
        title: 'Season Registration',
        description: 'Handle spring, summer, and fall registrations with age-based divisions.'
      },
      {
        title: 'Skill Development',
        description: 'Track stick skills, shooting, defense, and game IQ with progress assessments.'
      },
      {
        title: 'Team Management',
        description: 'Create teams, manage rosters, and handle tryout placements.'
      },
      {
        title: 'Tournament Tracking',
        description: 'Manage tournament entries, travel logistics, and team schedules.'
      },
      {
        title: 'Equipment Management',
        description: 'Track equipment needs and manage sizing for required gear.'
      },
      {
        title: 'Family Portal',
        description: 'Give families access to schedules, team information, and player progress.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage recreational and travel lacrosse?',
        answer:
          'Yes, handle rec leagues and competitive travel programs with separate registrations and skill tracking.'
      },
      {
        question: 'How do families register for lacrosse?',
        answer:
          'Parents register online, select programs, and pay fees. Collect player information and equipment needs.'
      },
      {
        question: 'Can I track lacrosse skills?',
        answer:
          'Yes, create skill assessments for stick work, shooting, defense, and lacrosse IQ. Track player development.'
      },
      {
        question: 'Does Leevo support camps and clinics?',
        answer: 'Absolutely. Run lacrosse camps, position clinics, and skills workshops with separate registration.'
      },
      {
        question: 'Can I manage multiple teams?',
        answer: 'Yes, organize players by age and skill level with roster management and team communications.'
      }
    ],
    benefits: [
      'Streamline registration',
      'Track player growth',
      'Manage tournaments',
      'Handle equipment',
      'Keep families informed'
    ]
  },

  wrestling: {
    value: 'wrestling',
    slug: 'wrestling',
    name: 'Wrestling',
    title: 'Wrestling Club Management Software',
    metaDescription:
      'Manage your wrestling club with Leevo. Handle registrations, track wrestler development, and streamline operations for youth and competitive wrestling programs.',
    keywords: [
      'wrestling software',
      'wrestling club management',
      'youth wrestling registration',
      'wrestling team software',
      'wrestling training software',
      'wrestling camp registration',
      'wrestler development tracking',
      'wrestling program management',
      'wrestling tournament software',
      'wrestling academy software'
    ],
    hero: {
      headline: 'Pin Down Your Wrestling Program Management',
      subheadline:
        'From youth wrestling to competitive clubs, manage registrations, track wrestler development, and run your program with discipline.'
    },
    features: [
      {
        title: 'Season Registration',
        description: 'Handle registrations with age and weight class divisions for youth and competitive wrestling.'
      },
      {
        title: 'Skill Tracking',
        description: 'Track technique development in takedowns, escapes, pins, and match strategy.'
      },
      {
        title: 'Weight Management',
        description: 'Track wrestler weights and manage weight class assignments safely.'
      },
      {
        title: 'Tournament Entries',
        description: 'Manage tournament registrations, weigh-in requirements, and competition schedules.'
      },
      {
        title: 'Training Programs',
        description: 'Run technique clinics, conditioning programs, and off-season training.'
      },
      {
        title: 'Match Records',
        description: 'Track match results, win-loss records, and tournament placements.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage youth and competitive wrestling?',
        answer:
          'Yes, handle recreational youth programs and competitive club wrestling with appropriate age and weight divisions.'
      },
      {
        question: 'How do wrestlers register?',
        answer:
          'Parents register online, select programs, and pay fees. Collect medical information and weight class preferences.'
      },
      {
        question: 'Can I track wrestling skill development?',
        answer:
          'Yes, create technique assessments and track development in takedowns, escapes, pins, and overall match ability.'
      },
      {
        question: 'Does Leevo support wrestling camps?',
        answer: 'Absolutely. Run technique camps, intensive training programs, and clinics with separate registration.'
      },
      {
        question: 'Can I track tournament results?',
        answer: 'Yes, record match results, tournament placements, and season records for each wrestler.'
      }
    ],
    benefits: [
      'Streamline registration',
      'Track technique development',
      'Manage weight classes',
      'Organize tournaments',
      'Keep families connected'
    ]
  },

  fencing: {
    value: 'fencing',
    slug: 'fencing',
    name: 'Fencing',
    title: 'Fencing Club Management Software',
    metaDescription:
      'Manage your fencing club with Leevo. Handle class registrations, track fencer development, and streamline operations for recreational and competitive fencing.',
    keywords: [
      'fencing software',
      'fencing club management',
      'fencing class registration',
      'fencing school software',
      'fencing training software',
      'fencing academy management',
      'competitive fencing software',
      'youth fencing programs',
      'fencing skill tracking',
      'fencing tournament management'
    ],
    hero: {
      headline: 'Sharpen Your Fencing Program Management',
      subheadline:
        'From beginner classes to competitive training, manage registrations, track fencer development, and run your salle with precision.'
    },
    features: [
      {
        title: 'Class Registration',
        description: 'Let fencers register for foil, epee, and sabre classes with online payment.'
      },
      {
        title: 'Skill Progression',
        description: 'Track technique development in footwork, bladework, tactics, and bout performance.'
      },
      {
        title: 'Competition Management',
        description: 'Manage tournament entries, ratings tracking, and competition schedules.'
      },
      {
        title: 'Equipment Tracking',
        description: 'Track loaner equipment, manage sizing, and handle equipment requirements.'
      },
      {
        title: 'Multi-Weapon Support',
        description: 'Manage separate programs for foil, epee, and sabre with weapon-specific skill tracking.'
      },
      {
        title: 'Private Lessons',
        description: 'Schedule individual lessons with coaches alongside group classes.'
      }
    ],
    faqs: [
      {
        question: 'Does Leevo support all three weapons?',
        answer: 'Yes, manage foil, epee, and sabre programs with weapon-specific classes, skills, and progressions.'
      },
      {
        question: 'How do fencers register for classes?',
        answer:
          'Fencers or parents register online, select classes and weapons, and pay fees. Handle beginner through advanced levels.'
      },
      {
        question: 'Can I track fencing skill development?',
        answer:
          'Yes, create skill assessments for footwork, bladework, tactics, and bouting. Track progression for each fencer.'
      },
      {
        question: 'Does Leevo support competitive fencing?',
        answer:
          'Absolutely. Track ratings, manage tournament entries, and organize competitive training alongside recreational classes.'
      },
      {
        question: 'Can I schedule private lessons?',
        answer: 'Yes, manage individual lesson scheduling with coaches alongside group class offerings.'
      }
    ],
    benefits: [
      'Fill classes with online registration',
      'Track fencer development',
      'Manage all three weapons',
      'Organize competitions',
      'Handle equipment logistics'
    ]
  },

  rowing: {
    value: 'rowing',
    slug: 'rowing',
    name: 'Rowing',
    title: 'Rowing Club Management Software',
    metaDescription:
      'Manage your rowing club with Leevo. Handle registrations, track rower development, and streamline operations for learn-to-row and competitive rowing programs.',
    keywords: [
      'rowing software',
      'rowing club management',
      'crew team software',
      'learn to row registration',
      'rowing program software',
      'sculling software',
      'rowing training software',
      'rowing camp registration',
      'rower development tracking',
      'regatta management'
    ],
    hero: {
      headline: 'Row Your Program to Success',
      subheadline:
        'From learn-to-row to competitive crews, manage registrations, track rower development, and keep your program moving forward.'
    },
    features: [
      {
        title: 'Program Registration',
        description: 'Handle learn-to-row, recreational, and competitive program registrations online.'
      },
      {
        title: 'Skill Development',
        description: 'Track technique progression in sculling, sweep, and erg performance.'
      },
      {
        title: 'Boat Assignments',
        description: 'Manage lineups, seat assignments, and crew compositions.'
      },
      {
        title: 'Regatta Management',
        description: 'Track regatta entries, race results, and competitive schedule.'
      },
      {
        title: 'Erg Tracking',
        description: 'Record erg scores and track performance improvements.'
      },
      {
        title: 'Safety Certification',
        description: 'Track swim tests, safety certifications, and required qualifications.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage learn-to-row and competitive programs?',
        answer:
          'Yes, handle introductory learn-to-row programs alongside competitive team training with appropriate skill tracking.'
      },
      {
        question: 'How do rowers register?',
        answer:
          'Rowers or parents register online, select programs, and pay fees. Collect swim test status and medical information.'
      },
      {
        question: 'Can I track rowing skill development?',
        answer:
          'Yes, create technique assessments for sculling, sweep, and general rowing ability. Track improvement over time.'
      },
      {
        question: 'Does Leevo track erg scores?',
        answer: 'Yes, record erg times and distances to track conditioning and compare performances.'
      },
      {
        question: 'Can I manage regatta entries?',
        answer: 'Yes, track race entries, boat assignments, and results throughout the competitive season.'
      }
    ],
    benefits: [
      'Streamline registration',
      'Track rower development',
      'Manage boat assignments',
      'Organize regattas',
      'Track erg performance'
    ]
  },

  climbing: {
    value: 'climbing',
    slug: 'climbing',
    name: 'Climbing',
    title: 'Climbing Gym Program Management Software',
    metaDescription:
      'Manage your climbing gym programs with Leevo. Handle class registrations, track climber progression, and streamline operations for youth and adult climbing programs.',
    keywords: [
      'climbing software',
      'climbing gym management',
      'climbing class registration',
      'youth climbing programs',
      'climbing team software',
      'bouldering software',
      'climbing skill tracking',
      'climbing camp registration',
      'climbing instruction software',
      'rock climbing management'
    ],
    hero: {
      headline: 'Scale New Heights with Better Program Management',
      subheadline:
        'From intro classes to competition teams, manage registrations, track climber progression, and grow your climbing program.'
    },
    features: [
      {
        title: 'Class Registration',
        description: 'Let climbers register for intro courses, skill classes, and team programs online.'
      },
      {
        title: 'Skill Progression',
        description: 'Track climbing ability through grade progressions and technique milestones.'
      },
      {
        title: 'Team Management',
        description: 'Manage youth and adult competition teams with practice schedules and competition entries.'
      },
      {
        title: 'Safety Certification',
        description: 'Track belay certifications, lead climbing qualifications, and safety requirements.'
      },
      {
        title: 'Camp Programs',
        description: 'Run climbing camps, clinics, and special events with easy registration.'
      },
      {
        title: 'Multi-Discipline Support',
        description: 'Manage bouldering, sport climbing, and speed climbing programs.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage different climbing disciplines?',
        answer:
          'Yes, handle bouldering, sport climbing, and speed climbing programs with appropriate skill tracking for each.'
      },
      {
        question: 'How do climbers register for classes?',
        answer: 'Climbers or parents register online, select programs and skill levels, and pay fees.'
      },
      {
        question: 'Can I track climbing progression?',
        answer: 'Yes, track grade progressions, technique milestones, and overall climbing ability development.'
      },
      {
        question: 'Does Leevo support climbing teams?',
        answer:
          'Absolutely. Manage competition teams with practice schedules, competition entries, and performance tracking.'
      },
      {
        question: 'Can I track certifications?',
        answer: 'Yes, track belay certifications, lead climbing qualifications, and other safety requirements.'
      }
    ],
    benefits: [
      'Fill classes with online registration',
      'Track climber progression',
      'Manage competition teams',
      'Handle certifications',
      'Run camps and clinics'
    ]
  },

  pickleball: {
    value: 'pickleball',
    slug: 'pickleball',
    name: 'Pickleball',
    title: 'Pickleball Program Management Software',
    metaDescription:
      'Manage your pickleball program with Leevo. Handle lesson registrations, court scheduling, and player development for clubs and recreation programs.',
    keywords: [
      'pickleball software',
      'pickleball lesson management',
      'pickleball registration',
      'pickleball club software',
      'pickleball court scheduling',
      'pickleball instruction',
      'pickleball camp registration',
      'pickleball training software',
      'pickleball skill tracking',
      'pickleball program management'
    ],
    hero: {
      headline: 'Serve Up Better Pickleball Program Management',
      subheadline:
        'From beginner clinics to competitive play, manage registrations, schedule courts, and grow your pickleball program.'
    },
    features: [
      {
        title: 'Lesson Registration',
        description: 'Let players register for clinics, group lessons, and private instruction online.'
      },
      {
        title: 'Court Scheduling',
        description: 'Manage court assignments for lessons, open play, and league events.'
      },
      {
        title: 'Skill Levels',
        description: 'Track player progression through rating levels with skill assessments.'
      },
      {
        title: 'League Management',
        description: 'Organize recreational and competitive leagues with standings and scheduling.'
      },
      {
        title: 'Multi-Program Support',
        description: 'Manage lessons, leagues, clinics, and tournaments in one system.'
      },
      {
        title: 'Player Matching',
        description: 'Help players find appropriate skill-level partners for lessons and play.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage pickleball lessons and leagues?',
        answer:
          'Yes, handle beginner lessons, skill clinics, recreational leagues, and competitive play all with online registration.'
      },
      {
        question: 'How do players register for pickleball?',
        answer:
          'Players register online, select programs or lessons, and pay fees. Handle individual and doubles registrations.'
      },
      {
        question: 'Can I track pickleball skill levels?',
        answer: 'Yes, create skill assessments to track player progression from beginner through advanced levels.'
      },
      {
        question: 'Does Leevo support tournaments?',
        answer: 'Absolutely. Manage tournament registrations, brackets, and results alongside regular programming.'
      },
      {
        question: 'Can I manage court scheduling?',
        answer: 'Yes, assign courts for lessons, clinics, leagues, and open play with visual scheduling tools.'
      }
    ],
    benefits: [
      'Fill programs with online registration',
      'Manage courts efficiently',
      'Track player development',
      'Organize leagues and tournaments',
      'Grow your pickleball community'
    ]
  },

  tumbling: {
    value: 'tumbling',
    slug: 'tumbling',
    name: 'Tumbling',
    title: 'Tumbling Class Management Software',
    metaDescription:
      'Manage your tumbling program with Leevo. Handle class registrations, track skill progressions, and process payments for recreational and competitive tumbling.',
    keywords: [
      'tumbling software',
      'tumbling class management',
      'tumbling registration',
      'acrobatics software',
      'tumbling skill tracking',
      'cheer tumbling software',
      'tumbling gym software',
      'power tumbling management',
      'tumbling instruction',
      'tumbling program management'
    ],
    hero: {
      headline: 'Flip Your Tumbling Program into High Gear',
      subheadline:
        'From beginner rolls to advanced aerials, manage class registrations, track skill progressions, and grow your tumbling program.'
    },
    features: [
      {
        title: 'Class Registration',
        description: 'Let athletes register for tumbling classes by age and skill level online.'
      },
      {
        title: 'Skill Progression',
        description:
          'Track development from basic rolls through advanced tumbling passes with detailed skill checklists.'
      },
      {
        title: 'Level Placement',
        description: 'Assess athletes and place them in appropriate skill-level classes.'
      },
      {
        title: 'Private Lessons',
        description: 'Manage individual skill-building sessions alongside group classes.'
      },
      {
        title: 'Cheer Integration',
        description: 'Coordinate tumbling training with cheer teams and competitive programs.'
      },
      {
        title: 'Safety Tracking',
        description: 'Document skill progressions and spot requirements for safe advancement.'
      }
    ],
    faqs: [
      {
        question: 'Can Leevo manage recreational and competitive tumbling?',
        answer:
          'Yes, handle beginner tumbling classes through advanced power tumbling and competitive programs with appropriate skill tracking.'
      },
      {
        question: 'How do athletes register for tumbling?',
        answer:
          'Parents register online, select class levels, and pay fees. Handle skill assessments for proper placement.'
      },
      {
        question: 'Can I track tumbling skill progression?',
        answer:
          'Yes, create detailed skill checklists from forward rolls through standing and running tumbling passes. Track mastery and readiness.'
      },
      {
        question: 'Does Leevo work for cheer tumbling?',
        answer:
          'Absolutely. Coordinate tumbling training with cheerleading programs and track skills needed for team placements.'
      },
      {
        question: 'Can I schedule private tumbling lessons?',
        answer: 'Yes, manage individual skill sessions with coaches alongside group class offerings.'
      }
    ],
    benefits: [
      'Fill classes with online registration',
      'Track skill progressions safely',
      'Place athletes appropriately',
      'Coordinate with cheer programs',
      'Manage private lessons'
    ]
  }
}

export function getSportContent(slug) {
  return SPORT_CONTENT[slug] || null
}

export function getAllSportContent() {
  return Object.values(SPORT_CONTENT)
}

export function getSportSlugs() {
  return Object.keys(SPORT_CONTENT)
}
